import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "playground-pseudo-locale-expanded";

type PlaygroundLocalizationContextValue = {
  expandText: boolean;
  setExpandText: (next: boolean) => void;
  toggleExpandText: () => void;
};

const PlaygroundLocalizationContext =
  createContext<PlaygroundLocalizationContextValue | null>(null);

const readStoredExpandText = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(STORAGE_KEY) === "true";
};

export const PlaygroundLocalizationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [expandText, setExpandTextState] = useState(readStoredExpandText);

  const setExpandText = useCallback((next: boolean) => {
    setExpandTextState(next);
    window.localStorage.setItem(STORAGE_KEY, String(next));
  }, []);

  const toggleExpandText = useCallback(() => {
    setExpandText(!expandText);
  }, [expandText, setExpandText]);

  const value = useMemo(
    () => ({
      expandText,
      setExpandText,
      toggleExpandText,
    }),
    [expandText, setExpandText, toggleExpandText]
  );

  return (
    <PlaygroundLocalizationContext.Provider value={value}>
      {children}
    </PlaygroundLocalizationContext.Provider>
  );
};

export const usePlaygroundLocalization =
  (): PlaygroundLocalizationContextValue => {
    const context = useContext(PlaygroundLocalizationContext);
    if (!context) {
      throw new Error(
        "usePlaygroundLocalization must be used within PlaygroundLocalizationProvider"
      );
    }

    return context;
  };
